/** Value formatting for the agent screens. Labels only, never a sentence. */

import type { CloudStatus } from "./client";

export const NONE = "None";

export function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

export function orNone(value: string | null | undefined): string {
  return value === null || value === undefined || value === "" ? NONE : value;
}

/** `123456` seconds as `1d 10h 17m`, trimmed to the two largest units present. */
export function duration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const parts: string[] = [];
  const days = Math.floor(whole / 86_400);
  const hours = Math.floor((whole % 86_400) / 3_600);
  const minutes = Math.floor((whole % 3_600) / 60);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (parts.length === 0) parts.push(`${whole}s`);
  return parts.slice(0, 2).join(" ");
}

/** Seconds until an ISO instant, floored at zero. */
export function secondsUntil(iso: string | null, now: number = Date.now()): number {
  if (iso === null) return 0;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? 0 : Math.max(0, Math.floor((at - now) / 1000));
}

/** `605` as `10:05`. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

/** An ISO instant as a local wall-clock time, or `None`. */
export function wallClock(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return NONE;
  const at = Date.parse(iso);
  return Number.isNaN(at) ? iso : new Date(at).toLocaleTimeString();
}

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export function cloudTone(status: string | undefined): Tone {
  const known = status as CloudStatus | undefined;
  if (known === "connected") return "success";
  if (known === "reconnecting") return "warning";
  if (known === "offline" || known === "unpaired") return "danger";
  return "neutral";
}
